import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bus,
  Wifi,
  WifiOff,
  RefreshCw,
  Clock,
  MapPin,
  User,
  Navigation,
  AlertCircle,
  ChevronLeft,
  Gauge,
  Radio,
  FlaskConical,
  X,
  Sparkles,
  KeyRound,
  Milestone,
  ParkingCircle,
  Wrench,
} from 'lucide-react';
import useFleetMonitor from '../hooks/trFleetMonitor';
import PageError from '../components/PageError';
import FleetMap from '../components/FleetMap';
import DayPlanningTimeline from '../components/DayPlanningTimeline';

// ─── Banner bêta pilote ────────────────────────────────────────────────────────
function BetaBanner() {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="flex items-start gap-3 px-4 py-3 rounded-xl bg-purple-50 border border-purple-200"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <FlaskConical className="w-4 h-4 text-purple-500 flex-shrink-0" />
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-300 flex-shrink-0">
          Bêta
        </span>
        <p className="text-sm text-purple-800">
          Cette fonctionnalité est en accès pilote.{' '}
          <span className="inline-flex items-center gap-1 font-medium">
            <Sparkles className="w-3 h-3" />
            Elle sera incluse dans le pack Premium
          </span>{' '}
          lors de son lancement commercial.
        </p>
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="p-1 rounded-lg hover:bg-purple-100 text-purple-400 hover:text-purple-600 transition-colors flex-shrink-0"
        title="Fermer"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

// ─── Config statuts ────────────────────────────────────────────────────────────
export const STATUS_CONFIG = {
  active_moving: {
    label: 'En trajet · En mouvement',
    dot: 'bg-green-500 animate-pulse',
    badge: 'bg-green-100 text-green-800',
    card: 'border-green-200',
  },
  active_stopped: {
    label: "En trajet · À l'arrêt",
    dot: 'bg-yellow-400',
    badge: 'bg-yellow-100 text-yellow-800',
    card: 'border-yellow-200',
  },
  parked_mid_trip: {
    label: 'Garé · Trajet en cours',
    dot: 'bg-amber-500 animate-pulse',
    badge: 'bg-amber-100 text-amber-700',
    card: 'border-amber-300',
  },
  service_moving: {
    label: 'Service · En mouvement',
    dot: 'bg-cyan-500 animate-pulse',
    badge: 'bg-cyan-100 text-cyan-800',
    card: 'border-cyan-200',
  },
  free_movement: {
    label: 'Service · En mouvement',
    dot: 'bg-cyan-500 animate-pulse',
    badge: 'bg-cyan-100 text-cyan-800',
    card: 'border-cyan-200',
  },
  service_stopped: {
    label: "Service · À l'arrêt",
    dot: 'bg-cyan-400',
    badge: 'bg-cyan-100 text-cyan-700',
    card: 'border-cyan-200',
  },
  idle: {
    label: 'Service · Moteur allumé',
    dot: 'bg-cyan-300',
    badge: 'bg-cyan-50 text-cyan-600',
    card: 'border-cyan-100',
  },
  parked: {
    label: 'Garé · Moteur coupé',
    dot: 'bg-gray-400',
    badge: 'bg-gray-100 text-gray-600',
    card: 'border-gray-200',
  },
  offline: {
    label: 'Hors ligne',
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-800',
    card: 'border-red-100',
  },
};

// ─── Badge statut ──────────────────────────────────────────────────────────────
export function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.offline;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ─── Carte bus dans la liste ───────────────────────────────────────────────────
export function BusListCard({ bus, isSelected, onClick }) {
  const speed = bus.position?.speed != null ? `${Math.round(bus.position.speed)} km/h` : '—';
  const lastSeen = bus.lastSeen
    ? new Date(bus.lastSeen).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : '—';
  const cfg = STATUS_CONFIG[bus.derivedStatus] || STATUS_CONFIG.offline;

  return (
    <motion.div
      layout
      onClick={() => onClick(bus)}
      className={`p-3 rounded-xl border-2 cursor-pointer transition-all duration-150 hover:shadow-md
        ${isSelected ? 'border-blue-400 bg-blue-50' : `bg-white ${cfg.card} hover:border-blue-300`}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              {
                active_moving: 'gradient-blue',
                active_stopped: 'bg-yellow-100',
                parked_mid_trip: 'bg-amber-100',
                service_moving: 'bg-cyan-100',
                free_movement: 'bg-cyan-100',
                service_stopped: 'bg-cyan-50',
                idle: 'bg-cyan-50',
                parked: 'bg-gray-100',
                offline: 'bg-gray-100',
              }[bus.derivedStatus] ?? 'bg-gray-100'
            }`}
          >
            <Bus
              className={`w-4 h-4 ${
                {
                  active_moving: 'text-white',
                  active_stopped: 'text-yellow-600',
                  parked_mid_trip: 'text-amber-600',
                  service_moving: 'text-cyan-600',
                  free_movement: 'text-cyan-600',
                  service_stopped: 'text-cyan-500',
                  idle: 'text-cyan-500',
                  parked: 'text-gray-400',
                  offline: 'text-gray-400',
                }[bus.derivedStatus] ?? 'text-gray-400'
              }`}
            />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">Bus {bus.busNumber || '—'}</p>
            {bus.route && (
              <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                {bus.route.name}
              </p>
            )}
          </div>
        </div>
        <StatusBadge status={bus.derivedStatus} />
      </div>

      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
        {bus.driver && (
          <span className="flex items-center gap-1 truncate">
            <User className="w-3 h-3 flex-shrink-0" />
            {bus.driver.name}
          </span>
        )}
        <span className="flex items-center gap-1 ml-auto flex-shrink-0">
          <Navigation className="w-3 h-3" />
          {speed}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          <Clock className="w-3 h-3" />
          {lastSeen}
        </span>
      </div>
    </motion.div>
  );
}

// ─── Panneau détail bus ────────────────────────────────────────────────────────
export function BusDetailPanel({ bus, onClose, onFocus }) {
  const speed = bus.position?.speed != null ? `${Math.round(bus.position.speed)} km/h` : '—';
  const heading = bus.position?.heading != null ? `${Math.round(bus.position.heading)}°` : '—';
  const lastSeen = bus.lastSeen
    ? new Date(bus.lastSeen).toLocaleString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
      })
    : '—';
  const ignition = bus.position?.ignition;
  const isBuffered = bus.position?.buffered === true;
  const odometer = bus.odometerKm != null ? `${Math.round(bus.odometerKm).toLocaleString('fr-FR')} km` : '—';

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-100 flex items-center gap-3">
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-gray-900">Bus {bus.busNumber}</p>
            {isBuffered && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                Différé
              </span>
            )}
          </div>
          <StatusBadge status={bus.derivedStatus} />
        </div>
        <button
          onClick={onFocus}
          title="Centrer la carte sur ce bus"
          className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors flex-shrink-0"
        >
          <Navigation className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="card-brand p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Informations</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Vitesse', value: speed },
              { label: 'Cap', value: heading },
              { label: 'Dernier signal', value: lastSeen },
              { label: 'Circuit', value: bus.route?.name || '—' },
              { label: 'Chauffeur', value: bus.driver?.name || '—' },
              { label: 'Trajet', value: bus.activeTrip ? 'Actif' : '—' },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-xs text-gray-400 font-medium">{label}</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="card-brand p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Télémétrie</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                <KeyRound className="w-3.5 h-3.5" />
                Moteur
              </span>
              {ignition === null || ignition === undefined ? (
                <span className="text-xs text-gray-400">—</span>
              ) : ignition ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  Allumé
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  Coupé
                </span>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                <Milestone className="w-3.5 h-3.5" />
                Kilométrage
              </span>
              <span className="text-xs font-semibold text-gray-700">{odometer}</span>
            </div>

            {isBuffered && (
              <div className="flex items-start gap-2 mt-1 p-2 rounded-lg bg-amber-50 border border-amber-200">
                <span className="text-amber-500 text-sm">⚠️</span>
                <p className="text-xs text-amber-700">
                  Position en différé — le tracker était hors connexion et a renvoyé cette position ultérieurement.
                </p>
              </div>
            )}
          </div>
        </div>

        {bus.route?.id && (
          <div className="card-brand p-4">
            <DayPlanningTimeline routeId={bus.route.id} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Ordre de tri par statut (priorité affichage) ──────────────────────────────
const STATUS_SORT_ORDER = {
  active_moving: 0,
  active_stopped: 1,
  parked_mid_trip: 2,
  service_moving: 3,
  free_movement: 3,
  service_stopped: 4,
  idle: 4,
  parked: 5,
  offline: 6,
};

export function sortBusesByStatus(buses) {
  return [...buses].sort(
    (a, b) => (STATUS_SORT_ORDER[a.derivedStatus] ?? 8) - (STATUS_SORT_ORDER[b.derivedStatus] ?? 8),
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────
const FleetMonitor = () => {
  const { buses, school, summary, isLoading, error, isConnected, lastUpdate, isInitialSyncing, refresh } =
    useFleetMonitor();
  const hasService = summary.service > 0;
  const [selectedBusId, setSelectedBusId] = useState(null);
  const [focusTrigger, setFocusTrigger] = useState(0);

  const selectedBus = selectedBusId ? (buses.find((b) => b.busId === selectedBusId) ?? null) : null;

  const handleBusClick = useCallback((bus) => setSelectedBusId(bus.busId), []);
  const handleCloseDetail = useCallback(() => setSelectedBusId(null), []);
  const handleFocus = useCallback(() => setFocusTrigger((t) => t + 1), []);

  const lastUpdateStr = lastUpdate
    ? lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

  const sortedBuses = sortBusesByStatus(buses);

  return (
    <div className="h-full flex flex-col space-y-4">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Flotte en temps réel</h1>
          <p className="text-sm text-gray-500 mt-0.5">Suivi GPS 24/7 de tous les bus</p>
        </div>

        <div className="flex items-center gap-3">
          {isInitialSyncing && (
            <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1">
              <RefreshCw className="w-3 h-3 animate-spin" />
              <span className="hidden sm:block">Synchronisation GPS…</span>
            </div>
          )}

          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            {isConnected ? <Wifi className="w-4 h-4 text-green-500" /> : <WifiOff className="w-4 h-4 text-red-400" />}
            <span className="text-xs hidden sm:block">{isConnected ? 'Connecté' : 'Déconnecté'}</span>
          </div>

          <span className="text-xs text-gray-400 hidden md:block">Mis à jour {lastUpdateStr}</span>

          <button
            onClick={refresh}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            title="Actualiser"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* ── Banner bêta ── */}
      <BetaBanner />

      {/* ── Compteurs ── */}
      <div className={`grid gap-3 ${hasService ? 'grid-cols-3 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4'}`}>
        {[
          {
            label: 'En trajet',
            count: summary.active,
            icon: Navigation,
            gradient: 'gradient-blue',
            text: 'text-white',
          },
          ...(hasService
            ? [
                {
                  label: 'Service',
                  count: summary.service,
                  icon: Wrench,
                  gradient: 'bg-cyan-100',
                  text: 'text-cyan-600',
                },
              ]
            : []),
          { label: 'Hors trajet', count: summary.idle, icon: Radio, gradient: 'bg-blue-100', text: 'text-blue-600' },
          { label: 'Garé', count: summary.parked, icon: ParkingCircle, gradient: 'bg-gray-100', text: 'text-gray-500' },
          {
            label: 'Hors ligne',
            count: summary.offline,
            icon: AlertCircle,
            gradient: 'bg-red-100',
            text: 'text-red-500',
          },
        ].map(({ label, count, icon: Icon, gradient, text }) => (
          <div key={label} className="card-brand p-3 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${gradient}`}>
              <Icon className={`w-4 h-4 ${text}`} />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{count}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Corps principal ── */}
      <div className="flex flex-col xl:flex-row gap-4 flex-1 xl:overflow-hidden xl:min-h-0">
        {/* Carte — hauteur fixe mobile/tablette, flex-1 desktop */}
        <div className="h-[280px] shrink-0 xl:h-auto xl:flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden xl:min-w-0">
          {isLoading ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-gray-400">
              <RefreshCw className="w-8 h-8 animate-spin" />
              <p className="text-sm">Chargement de la flotte…</p>
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center">
              <PageError
                error="Failed to fetch"
                subtitle="Le suivi en temps réel est indisponible. Réessayez pour reconnecter."
                onRetry={refresh}
                variant="inline"
              />
            </div>
          ) : (
            <FleetMap
              buses={buses}
              school={school}
              selectedBusId={selectedBusId}
              focusTrigger={focusTrigger}
              onBusClick={handleBusClick}
              height="100%"
            />
          )}
        </div>

        {/* Sidebar — pleine largeur mobile, w-80 desktop */}
        <div className="w-full xl:w-80 flex flex-col xl:overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200 min-h-[240px]">
          <AnimatePresence mode="wait">
            {selectedBus ? (
              <motion.div
                key="detail"
                className="flex-1 overflow-hidden flex flex-col"
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 20, opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <BusDetailPanel bus={selectedBus} onClose={handleCloseDetail} onFocus={handleFocus} />
              </motion.div>
            ) : (
              <motion.div
                key="list"
                className="flex-1 flex flex-col overflow-hidden"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -20, opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Gauge className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold text-gray-900">{summary.total} bus</span>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {sortedBuses.length === 0 && !isLoading && (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-400 gap-2">
                      <Bus className="w-8 h-8" />
                      <p className="text-sm">Aucun bus disponible</p>
                    </div>
                  )}
                  {sortedBuses.map((bus) => (
                    <BusListCard
                      key={bus.busId}
                      bus={bus}
                      isSelected={selectedBusId === bus.busId}
                      onClick={handleBusClick}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default FleetMonitor;
